import { useState, useCallback, useMemo, useEffect } from 'react';
import { evaluateTransferPair } from '../utils/transferMatcher';

// Einheitliche ID-Auflösung (Firestore-Doc-ID bevorzugt, sonst lokale id)
const txId = (t) => t._docId || String(t.id);

/**
 * Kapselt die State-Logik für das manuelle Verbinden von Transaktionen.
 *
 * @param {Object}   args
 * @param {Array}    args.txs       Alle geladenen Transaktionen
 * @param {Function} args.onLink    onLink(outTx, inTx) — persistiert, wirft bei Fehler
 * @param {Function} args.onUnlink  onUnlink(txA, txB) — persistiert, wirft bei Fehler
 */
export function useTransferLinking({ txs, onLink, onUnlink }) {
  const [linkMode,       setLinkModeState] = useState(false);
  const [selectedIds,    setSelectedIds]   = useState([]); // geordnet, max. 2
  const [pendingPatches, setPendingPatches] = useState(new Map());
  const [isLinking,      setIsLinking]     = useState(false);

  // ── Optimistische Sicht: txs + noch nicht bestätigte Patches ────────────────
  const effectiveTxs = useMemo(() => {
    if (pendingPatches.size === 0) return txs;
    return txs.map(t => {
      const patch = pendingPatches.get(txId(t));
      return patch ? { ...t, ...patch } : t;
    });
  }, [txs, pendingPatches]);

  // Patch verwerfen, sobald der echte Firestore-Snapshot den Zielzustand zeigt.
  // Liest pendingPatches nur über den Updater → keine Dep, kein Render-Loop.
  useEffect(() => {
    setPendingPatches(prev => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map(prev);
      for (const [id, patch] of prev) {
        const real = txs.find(t => txId(t) === id);
        if (!real) { next.delete(id); changed = true; continue; }
        const reconciled = Object.keys(patch).every(
          k => (real[k] ?? null) === (patch[k] ?? null)
        );
        if (reconciled) { next.delete(id); changed = true; }
      }
      return changed ? next : prev;
    });
  }, [txs]);

  // ── Verbinden-Modus ─────────────────────────────────────────────────────────
  const setLinkMode = useCallback((v) => {
    setLinkModeState(v);
    if (!v) setSelectedIds([]);
  }, []);

  // ── Auswahl ─────────────────────────────────────────────────────────────────
  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length < 2)   return [...prev, id];
      return [prev[1], id]; // dritte Auswahl ersetzt die älteste
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // ── Kandidat (genau 2 ausgewählt) ───────────────────────────────────────────
  const candidate = useMemo(() => {
    if (selectedIds.length !== 2) return null;
    const a = effectiveTxs.find(t => txId(t) === selectedIds[0]);
    const b = effectiveTxs.find(t => txId(t) === selectedIds[1]);
    if (!a || !b) return null;
    return evaluateTransferPair(a, b);
  }, [selectedIds, effectiveTxs]);

  // ── Vorschläge (genau 1 ausgewählt) ─────────────────────────────────────────
  const suggestionMap = useMemo(() => {
    const map = new Map();
    if (selectedIds.length !== 1) return map;
    const sel = effectiveTxs.find(t => txId(t) === selectedIds[0]);
    if (!sel) return map;
    for (const t of effectiveTxs) {
      const id = txId(t);
      if (id === selectedIds[0]) continue;
      if (t.typ !== 'einzahlung' && t.typ !== 'auszahlung') continue;
      const ev = evaluateTransferPair(sel, t);
      if (!ev.valid || ev.blockers.length > 0) continue;
      map.set(id, { score: ev.score, quality: ev.quality });
    }
    return map;
  }, [selectedIds, effectiveTxs]);

  // ── Verbinden ───────────────────────────────────────────────────────────────
  const confirmLink = useCallback(async () => {
    if (!candidate || candidate.blockers.length > 0) return;
    const { out, in: inn } = candidate;
    const outId = txId(out), innId = txId(inn);
    const groupId = `mtg_${outId}`;

    setIsLinking(true);
    setPendingPatches(prev => {
      const next = new Map(prev);
      next.set(outId, { linkedTxId: innId, manualLink: true, transferGroupId: groupId });
      next.set(innId, { linkedTxId: outId, manualLink: true, transferGroupId: groupId });
      return next;
    });

    try {
      await onLink(out, inn);
      setLinkMode(false);
    } catch (e) {
      setPendingPatches(prev => {
        const next = new Map(prev);
        next.delete(outId);
        next.delete(innId);
        return next;
      });
    } finally {
      setIsLinking(false);
    }
  }, [candidate, onLink, setLinkMode]);

  // ── Trennen ─────────────────────────────────────────────────────────────────
  const unlink = useCallback(async (txA, txB) => {
    const aId = txId(txA), bId = txId(txB);
    const cleared = { linkedTxId: null, manualLink: null, transferGroupId: null };

    setPendingPatches(prev => {
      const next = new Map(prev);
      next.set(aId, cleared);
      next.set(bId, cleared);
      return next;
    });

    try {
      await onUnlink(txA, txB);
    } catch (e) {
      setPendingPatches(prev => {
        const next = new Map(prev);
        next.delete(aId);
        next.delete(bId);
        return next;
      });
    }
  }, [onUnlink]);

  return {
    effectiveTxs,
    linkMode, setLinkMode,
    selectedIds: selectedSet,
    selectedCount: selectedIds.length,
    toggleSelect, clearSelection,
    candidate, suggestionMap,
    isLinking, confirmLink, unlink,
  };
}
